import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

type SupersededCourse = {
  supersededByCourse: {
    id: string;
    title: string;
    identifier: string | null;
  } | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  enrolmentId: number;
  supersededCourse: SupersededCourse;
};

export function TransferToSupersedingCourseModal({
  isOpen,
  onClose,
  enrolmentId,
  supersededCourse,
}: Props): React.ReactElement {
  const utils = trpcAdmin.useUtils();
  const toast = useToast();

  const transferMutation = trpcAdmin.enrolment.transferToSupersedingCourse.useMutation({
    async onSuccess() {
      toast({
        status: 'success',
        title: t`Enrolment has been transferred to the superseding course`,
      });

      await utils.enrolment.byIdWithResourceStatus.invalidate({
        enrolmentId,
      });
      await utils.enrolment.list.invalidate(undefined, { refetchType: 'active' });
      onClose();
    },
    onError(error) {
      toast({
        status: 'error',
        title: error.message || t`An error occurred while transferring the enrolment`,
      });
    },
  });

  const supersedingCourse = supersededCourse.supersededByCourse;

  return (
    <Modal size="2xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent padding={4}>
        <ModalHeader>
          <Trans>Transfer enrolment to superseding course</Trans>
          <ModalCloseButton margin={4} />
        </ModalHeader>

        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm">
              <Trans>
                Transfer this enrolment to{' '}
                <Text as="span" fontWeight="bold">
                  {supersedingCourse?.identifier} ({supersedingCourse?.title})
                </Text>
                ?
              </Trans>
            </Text>

            <Alert status="warning" borderRadius="md">
              <HStack>
                <AlertIcon />
              </HStack>
              <AlertDescription>
                <VStack align="start" spacing={1}>
                  <Text textStyle="sm" color="fg.muted">
                    <Trans>
                      The original enrolment will be withdrawn and a new enrolment will be created
                      in the superseding course. Completed units will be carried over.
                    </Trans>
                  </Text>
                  <Text textStyle="sm" color="fg.muted">
                    <Trans>
                      Any partially completed units will be reset — the student will need to start
                      those units again from the beginning.
                    </Trans>
                  </Text>
                </VStack>
              </AlertDescription>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter gap={4}>
          <Button type="button" variant="ghost" colorScheme="black" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            isLoading={transferMutation.isPending}
            onClick={() => transferMutation.mutate({ enrolmentId })}
          >
            <Trans>Transfer enrolment</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
